// honestTime — reconcile a recipe's AUTHORED time with the time its
// own step/timer graph actually adds up to (roadmap B6: honest time
// metadata). The model routinely writes `time: {prep: 10, cook: 20}`
// on a recipe whose steps carry 55 minutes of timers — the header
// lies, the cook plans dinner around the lie, and trust erodes.
//
// The step graph is ground truth: every timer was authored to be
// SEEN and RUN. Derivation: sum each step's clock (single timer or
// stacked timers — stages of one step, so they add), plus a small
// prep allowance per untimed step (grab/measure/combine work).
// Steps can overlap in a real kitchen ("while the sauce simmers…"),
// so we only correct when the authored total understates the graph
// by a wide margin (< 60%), and we never touch a recipe whose
// header is honest or generous.

const UNTIMED_STEP_MIN = 1.5;   // prep allowance per timer-less step
const UNDERSTATE_RATIO = 0.6;   // correct only below this fraction

// Passive timers (rise, chill, marinate…) deliberately live OUTSIDE
// time.prep/time.cook — the generator's own contract puts long
// passive spans in step timers while the header counts active work
// only (matches calendarUtils' active-time convention). Summing them
// into the comparison would "correct" every bread recipe's honest
// 20-minute header to 140 minutes. Braise/simmer/roast stay counted:
// they're attended cook time.
const PASSIVE_RE = /\b(chill|marinate|proof|rise|rest|cool|freeze|refrigerat|soak|brine|cure|ferment|overnight|sous.?vide)\b/i;

function isPassive(step, timerLabel) {
  const text = `${timerLabel || ""} ${step?.title || ""} ${step?.instruction || step?.body || ""}`;
  return PASSIVE_RE.test(text);
}

export function deriveGraphMinutes(recipe) {
  const steps = Array.isArray(recipe?.steps) ? recipe.steps : [];
  if (!steps.length) return 0;
  let timedSec = 0;
  let untimed = 0;
  for (const s of steps) {
    let stepSec = 0;
    if (Array.isArray(s?.timers) && s.timers.length > 0) {
      // Stacked timers: count each stage unless ITS label is passive
      // ("mix 3 min" counts; "proof 90 min" doesn't).
      for (const t of s.timers) {
        const sec = Number(t?.seconds) || 0;
        if (sec > 0 && !isPassive(s, t?.label)) stepSec += sec;
      }
    } else {
      const sec = Number(s?.timer) || 0;
      if (sec > 0 && !isPassive(s, null)) stepSec = sec;
    }
    if (stepSec > 0) timedSec += stepSec;
    else if (!(Number(s?.timer) > 0) && !(Array.isArray(s?.timers) && s.timers.length > 0)) untimed += 1;
  }
  return Math.round(timedSec / 60 + untimed * UNTIMED_STEP_MIN);
}

/**
 * Returns the recipe with an honest `time` block (and a `timeAudit`
 * stamp when a correction fired), or the same object untouched.
 */
export function applyHonestTime(recipe) {
  if (!recipe || typeof recipe !== "object") return recipe;
  const derivedMin = deriveGraphMinutes(recipe);
  if (derivedMin <= 0) return recipe;
  const prep = Number(recipe.time?.prep) || 0;
  const cook = Number(recipe.time?.cook) || 0;
  const authoredMin = prep + cook;
  if (authoredMin >= derivedMin * UNDERSTATE_RATIO) return recipe;
  // Header materially understates its own steps — replace cook time
  // so prep + cook covers the graph, rounded up to the nearest 5.
  const honestTotal = Math.ceil(derivedMin / 5) * 5;
  const honestPrep = prep > 0 ? prep : Math.min(10, Math.max(5, honestTotal - derivedMin + 5));
  const honestCook = Math.max(1, honestTotal - honestPrep);
  return {
    ...recipe,
    time: { ...(recipe.time || {}), prep: honestPrep, cook: honestCook },
    // Audit trail (beta transparency): what the model claimed vs
    // what its steps add up to. Diagnostic surfaces can render it.
    timeAudit: { authoredMin, derivedMin },
  };
}
